import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import {
  createItem,
  fetchPortfolio,
  removeItem,
  sendChatMessage,
  updateItem,
  updateProfile,
} from './api/portfolioApi';
import PublicPortfolioPage from './pages/PublicPortfolioPage';
import StudioPage from './pages/StudioPage';

export function AppShell({ initialEntries, initialPortfolio = null }) {
  const RouterComponent = initialEntries ? MemoryRouter : React.Fragment;
  const routerProps = initialEntries ? { initialEntries } : {};
  const [portfolio, setPortfolio] = useState(initialPortfolio);
  const [loading, setLoading] = useState(!initialPortfolio);
  const [error, setError] = useState('');

  const loadPortfolio = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchPortfolio();
      setPortfolio(response.portfolio);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load portfolio data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  const applyPortfolioMutation = useCallback((response) => {
    setPortfolio(response.portfolio);
    return response;
  }, []);

  const handleSaveProfile = useCallback(
    async (profile, studioKey) => {
      const response = await updateProfile(profile, studioKey);
      return applyPortfolioMutation(response);
    },
    [applyPortfolioMutation]
  );

  const handleCreateItem = useCallback(
    async (item, studioKey) => {
      const response = await createItem(item, studioKey);
      return applyPortfolioMutation(response);
    },
    [applyPortfolioMutation]
  );

  const handleUpdateItem = useCallback(
    async (itemId, item, studioKey) => {
      const response = await updateItem(itemId, item, studioKey);
      return applyPortfolioMutation(response);
    },
    [applyPortfolioMutation]
  );

  const handleDeleteItem = useCallback(
    async (itemId, studioKey) => {
      const response = await removeItem(itemId, studioKey);
      return applyPortfolioMutation(response);
    },
    [applyPortfolioMutation]
  );

  const shell = (
    <Routes>
      <Route
        path="/"
        element={
          <PublicPortfolioPage
            portfolio={portfolio}
            loading={loading}
            error={error}
            onRetry={loadPortfolio}
            onSendChat={sendChatMessage}
          />
        }
      />
      <Route
        path="/studio"
        element={
          <Navigate to="/backoffice" replace />
        }
      />
      <Route
        path="/jacq_sparo0"
        element={
          <StudioPage
            portfolio={portfolio}
            loading={loading}
            error={error}
            onRetry={loadPortfolio}
            onSaveProfile={handleSaveProfile}
            onCreateItem={handleCreateItem}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (initialEntries) {
    return <RouterComponent {...routerProps}>{shell}</RouterComponent>;
  }

  return shell;
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
